import { useEffect, useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { Section } from "../components/Section";
import { Seo } from "../components/Seo";
import { BoardMember, getBoardMembers } from "../data/board";
import { PtuLeader, getPtuLeadership } from "../data/ptu";
import { StaffMember, getStaff } from "../data/staff";

export function Staff() {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [board, setBoard] = useState<BoardMember[]>([]);
  const [ptu, setPtu] = useState<PtuLeader[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    getStaff({ useFallback: false })
      .then(setStaff)
      .catch((err) => {
        console.warn("[public-web] Staff page load failed", err);
        setError("Staff profiles could not be loaded right now.");
      })
      .finally(() => setLoading(false));
    getBoardMembers({ useFallback: false }).then(setBoard).catch(() => setBoard([]));
    getPtuLeadership({ useFallback: false }).then(setPtu).catch(() => setPtu([]));
  }, []);

  return (
    <>
      <Seo title="Staff" description="Faculty, staff, board, and PTU leadership at Zion Classical Academy." path="/staff" />
      <PageHeader eyebrow="Staff" title="Faculty and school leadership">
        ZCA is served by classroom teachers, support staff, school leadership, and a Board of Directors.
      </PageHeader>
      <Section title="Meet the team">
        {loading ? <p className="lead">Loading staff profiles...</p> : null}
        {!loading && error ? <p className="notice-light">{error}</p> : null}
        {!loading && !error && staff.length === 0 ? <p className="lead">Published staff profiles are not available right now.</p> : null}
        <div className="staff-grid">
          {staff.map((member) => (
            <article className="card staff-card" key={member.id}>
              {member.imageUrl ? (
                <img className="staff-photo" src={member.imageUrl} alt="" />
              ) : (
                <div className="avatar" aria-hidden="true">{member.name.slice(0, 1)}</div>
              )}
              <h3>{member.name}</h3>
              <p className="role">{member.role}</p>
              <p>{member.bio}</p>
              {member.email ? <p><a href={`mailto:${member.email}`}>{member.email}</a></p> : null}
            </article>
          ))}
        </div>
      </Section>
      <Section title="Board of Directors" className="band-warm">
        <div className="resource-grid">
          {board.map((member) => (
            <article className="card staff-card" key={`${member.name}-${member.role}`}>
              {member.imageUrl ? (
                <img className="staff-photo" src={member.imageUrl} alt="" />
              ) : (
                <div className="avatar" aria-hidden="true">{(member.name || member.role).slice(0, 1)}</div>
              )}
              <h3>{member.name}</h3>
              <p className="role">{member.role}</p>
              {member.bio ? <p>{member.bio}</p> : null}
              {member.email ? <p><a href={`mailto:${member.email}`}>{member.email}</a></p> : null}
            </article>
          ))}
        </div>
      </Section>
      <Section title="Parent Teachers United Leadership">
        <div className="resource-grid">
          {ptu.map((leader) => (
            <article className="card staff-card" key={`${leader.id ?? leader.role}-${leader.name}`}>
              {leader.imageUrl ? (
                <img className="staff-photo" src={leader.imageUrl} alt="" />
              ) : (
                <div className="avatar" aria-hidden="true">{(leader.name || leader.role || "Open").slice(0, 1)}</div>
              )}
              <h3>{leader.name || "Open"}</h3>
              <p className="role">{leader.role}</p>
              {leader.bio ? <p>{leader.bio}</p> : null}
              {leader.email ? <p><a href={`mailto:${leader.email}`}>{leader.email}</a></p> : null}
            </article>
          ))}
          {!ptu.length ? <p className="lead">PTU leadership information will be published soon.</p> : null}
        </div>
      </Section>
    </>
  );
}
